/**
 * Session state and the route guard.
 *
 * The console previously rendered for anybody who loaded the page, with a
 * hardcoded "Deivydas B. · superadmin · all brands" in the sidebar left over
 * from the mockup. On a public URL that is an unauthenticated ops console.
 *
 * Two rules here, and the second matters as much as the first:
 *
 *  1. No session, no console. `/auth/me` is the source of truth; a 401 sends
 *     you to sign-in.
 *  2. A session with NO brand memberships is not access. The API provisions a
 *     user on first sign-in with zero grants precisely so that being in the
 *     Workspace domain is not itself permission. The UI has to honour that
 *     rather than render an empty console and imply the data is missing.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { ApiError, apiGet } from '../api/client';

export type Role = 'superadmin' | 'brand_admin' | 'ops_agent' | 'auditor' | 'viewer' | 'service';

export interface Membership {
  brandId: string;
  brandSlug: string;
  role: Role;
}

export interface Session {
  userId: string;
  email: string;
  name: string | null;
  memberships: Membership[];
  /** `synvite` in synvite.zendesk.com; null when the server has no Zendesk. */
  zendeskSubdomain?: string | null;
}

export function useSession(): UseQueryResult<Session, ApiError> {
  return useQuery<Session, ApiError>({
    queryKey: ['session'],
    queryFn: () => apiGet<Session>('/auth/me'),
    // A 401 is an answer, not a failure worth retrying.
    retry: false,
    staleTime: 60_000,
  });
}

export function isSuperadmin(session: Session): boolean {
  return session.memberships.some((m) => m.role === 'superadmin');
}

/** "superadmin · all brands", "brand_admin · 2 brands", "no brand access". */
export function describeAccess(session: Session): string {
  if (session.memberships.length === 0) return 'no brand access';
  if (isSuperadmin(session)) return 'superadmin · all brands';

  const roles = [...new Set(session.memberships.map((m) => m.role))].sort();
  const brands = new Set(session.memberships.map((m) => m.brandId)).size;
  return `${roles.join(', ')} · ${brands} ${brands === 1 ? 'brand' : 'brands'}`;
}

export function initialsFor(session: Session): string {
  const source = session.name?.trim() || session.email;
  const parts = source.split(/[\s.@_-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
}

function Centred({ children }: { children: ReactNode }) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-paper px-6">
      <div className="w-full max-w-sm">{children}</div>
    </main>
  );
}

function NoAccess({ session }: { session: Session }) {
  return (
    <Centred>
      <h1 className="text-xl font-semibold tracking-tight text-ink-900">Zoye</h1>
      <div className="mt-6 rounded-lg bg-surface p-6 shadow-hairline shadow-raised">
        <h2 className="text-sm font-semibold text-ink-900">No brand access yet</h2>
        <p className="mt-2 text-xs text-ink-500">
          You are signed in as <span className="font-medium text-ink-900">{session.email}</span>,
          but your account has no brand membership. Signing in with a company account does not by
          itself grant access to customer data — an administrator has to add you to a brand.
        </p>
        <a
          href="/auth/logout"
          className="mt-5 inline-flex h-8 items-center rounded px-3 text-sm font-medium text-ink-700 shadow-hairline transition-colors duration-fast hover:bg-surface-sunken"
        >
          Sign out
        </a>
      </div>
    </Centred>
  );
}

/** Wraps every route that shows customer data. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { data, isPending, error } = useSession();

  if (isPending) {
    return (
      <Centred>
        <p className="text-center text-sm text-ink-400">Checking your session…</p>
      </Centred>
    );
  }

  // Any failure to establish a session — 401, network, malformed — sends you to
  // sign-in. Failing open here would be the whole bug again.
  if (error || !data) return <Navigate to="/signin" replace />;

  if (data.memberships.length === 0) return <NoAccess session={data} />;

  return <>{children}</>;
}
